import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #0a0e1a;
    --surface: #111827;
    --surface2: #1a2235;
    --border: rgba(255,255,255,0.07);
    --accent: #00d4ff;
    --accent2: #7b5ea7;
    --error: #ef4444;
    --text: #e8edf5;
    --muted: #6b7a99;
    --font-display: 'Syne', sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
  }

  body {
    background: var(--bg);
    color: var(--text);
    font-family: var(--font-display);
    min-height: 100vh;
  }

  .auth-page {
    min-height: 100vh;
    background: radial-gradient(ellipse at 20% 50%, rgba(0,212,255,0.04) 0%, transparent 60%),
                radial-gradient(ellipse at 80% 20%, rgba(123,94,167,0.06) 0%, transparent 50%),
                var(--bg);
    display: flex;
    flex-direction: column;
  }

  .auth-nav {
    padding: 20px 48px;
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(10,14,26,0.85);
    backdrop-filter: blur(12px);
  }

  .auth-nav-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 20px;
    font-weight: 800;
    color: var(--text);
    cursor: pointer;
  }

  .auth-nav-logo span { font-size: 26px; }

  .auth-nav-link {
    font-size: 14px;
    color: var(--muted);
    background: none;
    border: none;
    cursor: pointer;
    font-family: var(--font-display);
    transition: color 0.2s;
  }

  .auth-nav-link:hover { color: var(--text); }
  .auth-nav-link strong { color: var(--accent); }

  .auth-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
  }

  .auth-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 20px;
    padding: 48px;
    width: 100%;
    max-width: 420px;
  }

  .auth-title {
    font-size: 28px;
    font-weight: 800;
    margin-bottom: 8px;
    color: var(--text);
  }

  .auth-sub {
    font-size: 14px;
    color: var(--muted);
    margin-bottom: 36px;
    line-height: 1.5;
  }

  .btn-google {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    background: var(--surface2);
    color: var(--text);
    border: 1px solid var(--border);
    padding: 13px 20px;
    font-size: 15px;
    font-weight: 600;
    font-family: var(--font-display);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: 24px;
  }

  .btn-google:hover {
    border-color: rgba(255,255,255,0.2);
    background: rgba(255,255,255,0.05);
  }

  .google-icon {
    width: 20px;
    height: 20px;
  }

  .divider-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
  }

  .divider-line {
    flex: 1;
    height: 1px;
    background: var(--border);
  }

  .divider-text {
    font-size: 12px;
    color: var(--muted);
    font-family: var(--font-mono);
    letter-spacing: 0.05em;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .form-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: var(--muted);
    margin-bottom: 8px;
    font-family: var(--font-mono);
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .form-input {
    width: 100%;
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 13px 16px;
    font-size: 15px;
    color: var(--text);
    font-family: var(--font-display);
    transition: border-color 0.2s;
    outline: none;
  }

  .form-input:focus {
    border-color: rgba(0,212,255,0.4);
  }

  .form-input::placeholder {
    color: var(--muted);
  }

  .form-input.error {
    border-color: var(--error);
  }

  .error-msg {
    font-size: 13px;
    color: var(--error);
    margin-top: 16px;
    padding: 12px 16px;
    background: rgba(239,68,68,0.08);
    border: 1px solid rgba(239,68,68,0.2);
    border-radius: 8px;
    line-height: 1.5;
  }

  .success-msg {
    font-size: 13px;
    color: #22c55e;
    margin-top: 16px;
    padding: 12px 16px;
    background: rgba(34,197,94,0.08);
    border: 1px solid rgba(34,197,94,0.2);
    border-radius: 8px;
    line-height: 1.5;
  }

  .btn-submit {
    width: 100%;
    background: linear-gradient(135deg, var(--accent), var(--accent2));
    color: white;
    border: none;
    padding: 14px 20px;
    font-size: 15px;
    font-weight: 700;
    font-family: var(--font-display);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;
    margin-top: 24px;
  }

  .btn-submit:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(0,212,255,0.25);
  }

  .btn-submit:disabled {
    opacity: 0.6;
    cursor: default;
  }

  .terms {
    font-size: 12px;
    color: var(--muted);
    text-align: center;
    margin-top: 16px;
    line-height: 1.5;
  }
`;

export default function Signup() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [firstName, setFirstName] = useState("");

  const handleGoogleSignup = async () => {
    setError("");
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/dashboard` }
    });
    if (error) setError(error.message);
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: `${window.location.origin}/dashboard`,
      data: { first_name: firstName }
    }
  });
    setLoading(false);
    if (error) {
      setError(error.message);
    } else {
      setSuccess("Check your email for a confirmation link to complete your sign up!");
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="auth-page">
        <nav className="auth-nav">
          <div className="auth-nav-logo" onClick={() => navigate("/")}>
            <span>🐧</span> PassPenguin
          </div>
          <button className="auth-nav-link" onClick={() => navigate("/login")}>
            Already have an account? <strong>Log in</strong>
          </button>
        </nav>

        <div className="auth-container">
          <div className="auth-card">
            <h1 className="auth-title">Create your account</h1>
            <p className="auth-sub">Start preparing for your Salesforce certification today.</p>

            <button className="btn-google" onClick={handleGoogleSignup}>
              <svg className="google-icon" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div className="divider-row">
              <div className="divider-line" />
              <span className="divider-text">or</span>
              <div className="divider-line" />
            </div>

            <form onSubmit={handleEmailSignup}>
              <div className="form-group">
                <label className="form-label">First name</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="Alex"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  className="form-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  className="form-input"
                  type="password"
                  placeholder="Min. 8 characters"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>

              {error && <p className="error-msg">{error}</p>}
              {success && <p className="success-msg">{success}</p>}

              <button className="btn-submit" type="submit" disabled={loading}>
                {loading ? "Creating account..." : "Create account →"}
              </button>
            </form>

            <p className="terms">
              By signing up you agree to our terms of service and refund policy.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}